import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    CheckCircleIcon, ClockIcon, UserIcon, UsersIcon, CubeTransparentIcon,
    AcademicCapIcon, MagnifyingGlassIcon, PresentationChartLineIcon,
    PuzzlePieceIcon, ClipboardDocumentListIcon, QuestionMarkCircleIcon,
    BookOpenIcon, BriefcaseIcon, AdjustmentsHorizontalIcon,
    ClipboardDocumentCheckIcon, InformationCircleIcon
} from '@heroicons/react/24/outline';
import { Lightbulb } from 'lucide-react';

const pathwayOptionsData = {
    recommended: [
        {
            id: 'teach',
            title: 'Teach',
            description: <>Best for explaining new ideas or sharing information. The AI will focus on presenting content clearly to the learner.</>,
            icon: Lightbulb,
            emoji: '📚',
        },
        {
            id: 'practice',
            title: 'Practice',
            description: <>Ideal for letting learners try things out and apply what they've learned. The AI will create activities and exercises.</>,
            icon: AdjustmentsHorizontalIcon,
            emoji: '📖',
        },
        {
            id: 'assess',
            title: 'Assess',
            description: <>Useful for checking what learners know or understand. The AI will generate questions like quizzes or short tests.</>,
            icon: ClipboardDocumentCheckIcon,
            emoji: '📝',
        },
        {
            id: 'copy',
            title: 'Copy My Lesson',
            description: 'Create a lesson based on an existing lesson structure.',
            icon: ClipboardDocumentListIcon,
            emoji: '📋',
        },
    ],
    advanced: [
        {
            id: 'direct',
            title: 'Direct Instruction',
            description: 'Present information directly to learners (e.g., content, video).',
            icon: PresentationChartLineIcon,
            emoji: '👨‍🏫',
            comingSoon: true
        },
        {
            id: 'tutor',
            title: 'Conversational',
            description: 'AI acts as a tutor, guiding the learner.',
            icon: AcademicCapIcon,
            emoji: '💬',
            comingSoon: true
        },
        {
            id: 'independent',
            title: 'Independent Practice',
            description: 'Learners practice applying knowledge on their own.',
            icon: UserIcon,
            emoji: '🧘',
            comingSoon: true
        },
        {
            id: 'guided',
            title: 'Guided Practice',
            description: 'Learners follow steps or examples to practice.',
            icon: UsersIcon,
            emoji: '🤝',
            comingSoon: true
        },
        {
            id: 'diagnostic',
            title: 'Diagnostic',
            description: 'Assess prior knowledge or identify learning gaps.',
            icon: ClipboardDocumentListIcon,
            emoji: '🩺',
            comingSoon: true
        },
        {
            id: 'quiz',
            title: 'Pop Quiz / Class Test',
            description: 'Short, focused assessment of understanding.',
            icon: QuestionMarkCircleIcon,
            emoji: '❓',
            comingSoon: true
        },
        {
            id: 'simulation',
            title: 'Simulation',
            description: 'Learners interact with a simulated environment.',
            icon: CubeTransparentIcon,
            emoji: '🕹️',
            comingSoon: true
        },
        {
            id: 'inquiry',
            title: 'Inquiry Based',
            description: 'Learners investigate questions or problems.',
            icon: MagnifyingGlassIcon,
            emoji: '🔎',
            comingSoon: true
        },
        {
            id: 'challenge',
            title: 'Challenge',
            description: 'Present learners with a difficult task or problem.',
            icon: PuzzlePieceIcon,
            emoji: '🧩',
            comingSoon: true
        },
        {
            id: 'open-book',
            title: 'Open book Assessment',
            description: 'Assess application of knowledge with resources available.',
            icon: BookOpenIcon,
            emoji: '📖',
            comingSoon: true
        },
        {
            id: 'project',
            title: 'Project',
            description: 'Assess skills through a larger, applied task.',
            icon: BriefcaseIcon,
            emoji: '🏗️',
            comingSoon: true
        },
        {
            id: 'essay',
            title: 'Essay',
            description: 'Assess understanding and argumentation through writing.',
            icon: ClipboardDocumentListIcon,
            emoji: '✍️',
            comingSoon: true
        },
        {
            id: 'scenario',
            title: 'Scenario (Simulation)',
            description: 'Apply knowledge in a simulated situation.',
            icon: CubeTransparentIcon,
            emoji: '🎭',
            comingSoon: true
        },
    ],
};

const mainCategories = ['recommended', 'advanced'];

const PathwayTypeStep = ({ selectedType, onTypeSelect, topicAnalysisResult }) => {

    const handleSelect = (typeObject) => {
        onTypeSelect(typeObject);
    };

    const renderOptionsList = (categoryKey) => {
        const options = pathwayOptionsData[categoryKey] || [];
        return (
            <div className="mb-4 space-y-3">
                <div className="flex flex-col space-y-3">
                    {options.map((option) => {
                        const isSelected = selectedType?.id === option.id;
                        const isDisabled = option.comingSoon;
                        const descriptionToShow = option.description;
                        const emoji = option.emoji || '❓';

                        return (
                            <div
                                key={option.id}
                                onClick={() => !isDisabled && handleSelect(option)}
                                className={`border rounded-lg p-4 flex flex-col justify-between relative transition-all min-h-[120px]
                              ${isDisabled ? 'bg-accent cursor-not-allowed opacity-60' : 'bg-background cursor-pointer hover:shadow-md hover:border-purple-300 dark:hover:border-purple-600'}
                              ${isSelected && !isDisabled ? 'border-purple-300 dark:border-purple-600 border-2 shadow-md' : 'border-border'} `}
                            >
                                <div>
                                    <div className="flex items-start space-x-3">
                                        <span className="text-2xl">{emoji}</span>
                                        <h4 className="font-semibold text-lg flex-grow">{option.title}</h4>
                                    </div>
                                    <div className="text-md text-primary/80 pl-9">
                                        {typeof descriptionToShow === 'string' ? <p>{descriptionToShow}</p> : descriptionToShow}
                                    </div>
                                </div>
                                {option.comingSoon && (
                                    <div className="flex items-center text-xs text-purple-600 dark:text-purple-400 italic mt-2 font-medium pl-9">
                                        <ClockIcon className="h-4 w-4 mr-1" />
                                        Coming Soon
                                    </div>
                                )}
                                {isSelected && !isDisabled && (
                                    <CheckCircleIcon className="h-5 w-5 text-purple-600 absolute top-2 right-2" />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-4">
            <h2 className="text-2xl font-medium mb-1">What type of lesson do you want to create?</h2>
            <p className="text-lg text-gray-600 font-light mb-4">
                Our AI will structure the content based on your selection.
            </p>

            <Tabs defaultValue={mainCategories[0]} className="w-full">
                <TabsList>
                    {mainCategories.map((category) => {
                        return (
                            <TabsTrigger key={category} value={category}>
                                {category.replace(/^./, str => str.toUpperCase())}
                            </TabsTrigger>
                        );
                    })}
                </TabsList>

                {topicAnalysisResult?.topicsSummaries?.length > 0 && (
                    <div className="mt-4 p-4 border border-blue-200 rounded-lg bg-blue-50">
                        <div className="flex items-start space-x-3">
                            <InformationCircleIcon className="h-6 w-6 text-blue-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                            <div className="flex-1">
                                <h4 className="text-sm font-medium text-blue-800 mb-1">
                                    {topicAnalysisResult.multipleTopics
                                        ? "Detected Multiple Topics in Document:"
                                        : "Detected Main Topic in Document:"
                                    }
                                </h4>
                                <ul className="list-disc list-inside space-y-1 text-sm text-blue-700">
                                    {topicAnalysisResult.topicsSummaries.map((topic, index) => (
                                        <li key={index}>
                                            <span className="font-medium">{topic.title}:</span> {topic.description}
                                        </li>
                                    ))}
                                </ul>
                                {topicAnalysisResult.multipleTopics && (
                                    <p className="text-xs text-blue-600 mt-2 italic">
                                        Consider if a multi-part pathway or separate pathways might be suitable.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className="mt-2">
                    {mainCategories.map((category) => (
                        <TabsContent key={category} value={category}>
                            {renderOptionsList(category)}
                        </TabsContent>
                    ))}
                </div>
            </Tabs>

        </div>
    );
};

export default PathwayTypeStep; 
